import React, { useState, useEffect } from 'react';
import { 
    Card, 
    List, 
    Typography, 
    Tag, 
    Avatar, 
    Space, 
    Select, 
    DatePicker, 
    Button, 
    Empty, 
    Spin, 
    Row, 
    Col, 
    Statistic,
    Timeline,
    Divider,
    Tooltip
} from 'antd';
import { 
    CalendarOutlined, 
    FileTextOutlined, 
    CheckCircleOutlined, 
    StarOutlined, 
    UserOutlined, 
    ShopOutlined, 
    ShoppingCartOutlined, 
    BellOutlined, 
    MessageOutlined, 
    SettingOutlined,
    FilterOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ActivityPage = () => {
    const { user } = useAuth();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [filters, setFilters] = useState({
        type: '',
        days: 30,
        dateRange: null
    });

    useEffect(() => {
        fetchActivities();
        fetchStats();
    }, [filters]);

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const params = {
                limit: 50,
                days: filters.days
            };
            
            if (filters.type) {
                params.type = filters.type;
            }
            
            if (filters.dateRange) {
                params.start_date = filters.dateRange[0].format('Y-MM-DD');
                params.end_date = filters.dateRange[1].format('Y-MM-DD');
            }

            const response = await api.getActivities(params);
            if (response.success) {
                setActivities(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch activities:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.getActivityStats({ days: filters.days });
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch activity stats:', error);
        }
    };

    const getActivityIcon = (iconName, color = 'blue') => {
        const icons = {
            'CalendarOutlined': <CalendarOutlined style={{ color }} />,
            'FileTextOutlined': <FileTextOutlined style={{ color }} />,
            'CheckCircleOutlined': <CheckCircleOutlined style={{ color }} />,
            'StarOutlined': <StarOutlined style={{ color }} />,
            'UserOutlined': <UserOutlined style={{ color }} />,
            'ShopOutlined': <ShopOutlined style={{ color }} />,
            'ShoppingCartOutlined': <ShoppingCartOutlined style={{ color }} />,
            'BellOutlined': <BellOutlined style={{ color }} />,
            'MessageOutlined': <MessageOutlined style={{ color }} />,
            'SettingOutlined': <SettingOutlined style={{ color }} />,
        };
        return icons[iconName] || <BellOutlined style={{ color }} />;
    };

    const getSeverityColor = (severity) => {
        const colors = {
            'success': 'green',
            'warning': 'orange',
            'error': 'red',
            'info': 'blue'
        };
        return colors[severity] || 'blue';
    };

    const getTypeLabel = (type) => {
        const labels = {
            'booking_created': 'Booking Created',
            'quote_sent': 'Quote Sent',
            'quote_accepted': 'Quote Accepted',
            'quote_rejected': 'Quote Rejected',
            'review_submitted': 'Review Submitted',
            'profile_updated': 'Profile Updated',
            'vendor_registered': 'Vendor Registered',
            'payment_processed': 'Payment Processed',
            'booking_confirmed': 'Booking Confirmed',
            'booking_cancelled': 'Booking Cancelled'
        };
        return labels[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const activityTypes = [
        { value: '', label: 'All Activities' },
        { value: 'booking_created', label: 'Bookings' },
        { value: 'quote_sent', label: 'Quotes Sent' },
        { value: 'quote_accepted', label: 'Quotes Accepted' },
        { value: 'review_submitted', label: 'Reviews' },
        { value: 'profile_updated', label: 'Profile Updates' },
        { value: 'vendor_registered', label: 'Vendor Registration' }
    ];

    const timeRanges = [
        { value: 7, label: 'Last 7 days' },
        { value: 30, label: 'Last 30 days' },
        { value: 90, label: 'Last 90 days' },
        { value: 365, label: 'Last year' }
    ];

    return (
        <div className="px-4 sm:px-6 py-6">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <Title level={2}>
                        <BellOutlined className="mr-2" />
                        Activity History
                    </Title>
                    <Text type="secondary">
                        Track all your activities and interactions
                    </Text>
                </div>

                {/* Stats Cards */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Total Activities"
                                value={stats.total_activities || 0}
                                prefix={<BellOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Bookings"
                                value={stats.bookings_created || 0}
                                prefix={<CalendarOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Quotes"
                                value={(stats.quotes_sent || 0) + (stats.quotes_accepted || 0)}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Reviews"
                                value={stats.reviews_submitted || 0}
                                prefix={<StarOutlined />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="mb-6" size="small">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={8} md={6}>
                            <Text strong>Activity Type:</Text>
                            <Select
                                value={filters.type}
                                onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
                                style={{ width: '100%' }}
                                placeholder="All Activities"
                            >
                                {activityTypes.map(type => (
                                    <Option key={type.value} value={type.value}>
                                        {type.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Text strong>Time Range:</Text>
                            <Select
                                value={filters.days}
                                onChange={(value) => setFilters(prev => ({ ...prev, days: value, dateRange: null }))}
                                style={{ width: '100%' }}
                            >
                                {timeRanges.map(range => (
                                    <Option key={range.value} value={range.value}>
                                        {range.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Text strong>Custom Date:</Text>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates, days: null }))}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6}>
                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={fetchActivities}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Activities List */}
                <Card title="Recent Activities" size="small">
                    {loading ? (
                        <div className="text-center py-8">
                            <Spin size="large" />
                        </div>
                    ) : activities.length === 0 ? (
                        <Empty 
                            description="No activities found"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <Timeline
                            items={activities.map(activity => ({
                                dot: (
                                    <div className="w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center shadow-sm"
                                         style={{ borderColor: activity.color }}
                                    >
                                        {getActivityIcon(activity.icon, activity.color)}
                                    </div>
                                ),
                                children: (
                                    <div className="pl-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Text strong className="text-base">
                                                        {activity.title}
                                                    </Text>
                                                    <Tag color={getSeverityColor(activity.severity)}>
                                                        {getTypeLabel(activity.type)}
                                                    </Tag>
                                                </div>
                                                <Text type="secondary" className="block mb-2">
                                                    {activity.description}
                                                </Text>
                                                {activity.data && Object.keys(activity.data).length > 0 && (
                                                    <div className="text-xs text-gray-500 mb-2">
                                                        {Object.entries(activity.data).map(([key, value]) => (
                                                            <span key={key} className="mr-4">
                                                                <Text code>{key}:</Text> {String(value)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 text-xs text-gray-400">
                                                    <span>{activity.time_ago}</span>
                                                    {activity.user && (
                                                        <span>by {activity.user.name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }))}
                        />
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ActivityPage;
